import { Text, makeStyles } from '@fluentui/react-components';
import { Checkmark16Filled } from '@fluentui/react-icons';

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '0',
    margin: '0',
    listStyle: 'none',
  },
  item: {
    backgroundColor: '#1D305A',
    borderRadius: '8px',
    transitionProperty: 'opacity, transform',
    transitionDuration: '0.2s, 0.1s',
    transitionTimingFunction: 'ease-in-out',
    ':hover': {
      opacity: 0.9,
    },
    ':active': {
      transform: 'scale(0.98)',
    },
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px',
    width: '100%',
    color: '#ffffff',
    cursor: 'pointer',
  },
  radioInput: {
    position: 'absolute',
    opacity: 0,
    width: '1px',
    height: '1px',
  },
  radioCircle: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  checkIcon: {
    color: '#50BCBD',
    flexShrink: 0,
    fontSize: '20px',
    filter: 'drop-shadow(0 0 8px #50BCBD) drop-shadow(0 0 12px #50BCBD)',
  },
  text: {
    color: '#ffffff',
    flex: 1,
    pointerEvents: 'none',
  },
});

type RadioOption = {
  value: string;
  label: string;
};

type RadioButtonListProps = {
  options: RadioOption[];
  selectedValue?: string;
  onChange: (value: string | undefined) => void;
  name: string;
};

export const RadioButtonList = ({
  options,
  selectedValue,
  onChange,
  name,
}: RadioButtonListProps) => {
  const styles = useStyles();

  const handleClick = (value: string, event: React.MouseEvent<HTMLLabelElement>) => {
    event.preventDefault();
    // If the clicked option is already selected, deselect it
    if (selectedValue === value) {
      onChange(undefined);
    } else {
      onChange(value);
    }
  };

  return (
    <ul className={styles.list} role="radiogroup" aria-label={name}>
      {options.map((option) => {
        const isSelected = selectedValue === option.value;
        return (
          <li
            key={option.value}
            className={styles.item}
            style={{
              border: isSelected ? '2px solid #50BCBD' : '2px solid transparent',
            }}
          >
            <label className={styles.label} onClick={(event) => handleClick(option.value, event)}>
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={() => {}} // Controlled by label click
                className={styles.radioInput}
                aria-checked={isSelected}
                tabIndex={0}
              />
              <span className={styles.radioCircle} aria-hidden="true">
                {isSelected && (
                  <Checkmark16Filled className={styles.checkIcon} aria-hidden="true" />
                )}
              </span>
              <Text size={200} weight="regular" className={styles.text}>
                {option.label}
              </Text>
            </label>
          </li>
        );
      })}
    </ul>
  );
};
